import { useEffect, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { CurrencyInput } from 'react-currency-mask'
import InputMask from 'react-input-mask'
import { ptBR } from 'date-fns/locale/pt-BR'
import 'react-datepicker/dist/react-datepicker.css'
import openedEyeIcon from '../../../assets/icons/opened_eye.svg'
import closedEyeIcon from '../../../assets/icons/closed_eye.svg'
import './styles.sass'
registerLocale('ptBR', ptBR)

const toDateForDatePicker = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();

    // Se vier ISO datetime (ex.: 2025-01-01T00:00:00.000Z), trata como "date-only"
    // pegando apenas YYYY-MM-DD para evitar shift de timezone.
    const datePart = /^\d{4}-\d{2}-\d{2}T/.test(trimmed) ? trimmed.slice(0, 10) : trimmed;

    // Trata YYYY-MM-DD como data pura (local)
    const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);
      if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
        return new Date(year, month - 1, day, 0, 0, 0, 0);
      }
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
};

const parsePtBrDateInput = (raw: string, withTime: boolean): Date | null => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;

  const match = withTime
    ? trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/)
    : trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const hour = withTime ? Number(match[4]) : 0;
  const minute = withTime ? Number(match[5]) : 0;

  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
  if (withTime && (!Number.isFinite(hour) || !Number.isFinite(minute))) return null;

  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  const isSameCalendarDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  if (!isSameCalendarDate) return null;
  if (withTime && (date.getHours() !== hour || date.getMinutes() !== minute)) return null;

  return date;
};

interface InputOptions { 
  name: string,
  label?: string,
  placeholder?: string,
  type?: string,
  dateFormat?: string,
  showTimeSelect?: boolean,
  value?: any,
  mask?: string,
  readonly?: boolean,
  disabled?: boolean,
  icon?: any,
  required?: boolean,
  onChange?: (value: any) => any,
  onKeyDown?: (e: React.KeyboardEvent) => void,
  min?: number,
  max?: number,
  maxLength?: number
}

export const Input = ({ name, label, placeholder, type, dateFormat, showTimeSelect, value, icon, mask, readonly, disabled, required, onChange, onKeyDown, min, max, maxLength }: InputOptions) => {
  const[currentValue, setCurrentValue] = useState(value || '')
  const[showPassword, togglePassword] = useState(false)

  const onInputChange = (e: any) => {
    onChange && onChange(e.target.value);
    setCurrentValue(e.target.value);
  }

  useEffect(() => {
    setCurrentValue(value || '');
  }, [value])

  const containerClassName = [
    'input-container',
    type,
    disabled ? 'disabled' : '',
    readonly ? 'readonly' : '',
    icon ? 'with-icon' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassName}>
      {label && <label>{label}{required && <span className="required-asterisk">*</span>}</label>}
      <div className='input'>
        {icon && <img className='icon' src={icon} alt='input-icon' />}
        {type === 'date' &&
          <DatePicker
          locale='ptBR'
          readOnly={readonly}
          selected={toDateForDatePicker(value)}
          placeholderText={placeholder}
          disabled={disabled}
          showTimeSelect={showTimeSelect}
          dateFormat={dateFormat ? dateFormat : showTimeSelect ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'}
          onChange={(date) => onInputChange({ target: { value: date } })}
          onKeyDown={onKeyDown}
          onChangeRaw={(e: any) => {
            const rawValue = e?.target?.value as string | undefined;
            const parsed = parsePtBrDateInput(rawValue || '', !!showTimeSelect);
            if (parsed) onInputChange({ target: { value: parsed } });
          }} />
        }
        {
          type === 'money' &&
          <CurrencyInput
            value={currentValue || ''}
            InputElement={
              <input
              name={name}
              placeholder={placeholder}
              value={currentValue || ''}
              disabled={disabled}
              readOnly={readonly}
              autoComplete="off" />
            }
            onChangeValue={(event, originalValue, maskedValue) => {
              onInputChange({ target: { value: originalValue } });
            }}
          />
        }
        {
          type === 'textarea' &&
          <textarea
          name={name}
          placeholder={placeholder}
          value={currentValue || ''}
          disabled={disabled}
          readOnly={readonly}
          onChange={onInputChange}
          onKeyDown={onKeyDown}>
          </textarea>
        }
        {
          (!type || !['date', 'money', 'textarea', 'password'].includes(type)) && !mask &&
          <input
          name={name}
          type={type && !showPassword ? type : 'text'}
          placeholder={placeholder}
          value={currentValue || ''}
          disabled={disabled}
          readOnly={readonly}
          {...(type === 'number' && { min, max })}
          {...(maxLength != null && { maxLength })}
          onChange={onInputChange}
          onKeyDown={onKeyDown} />
        }
        {
          type === 'password' &&
          <input
          name={name}
          type={type && !showPassword ? type : 'text'}
          placeholder={placeholder}
          value={currentValue || ''}
          disabled={disabled}
          readOnly={readonly}
          autoComplete='new-password'
          onChange={onInputChange}
          onKeyDown={onKeyDown} />
        }
        {
          (!type || !['date', 'money', 'textarea'].includes(type)) && mask &&
          <InputMask
          name={name}
          placeholder={placeholder}
          value={currentValue || ''}
          disabled={disabled}
          readOnly={readonly}
          mask={mask}
          {...(maxLength != null && { maxLength })}
          onChange={onInputChange}
          onKeyDown={onKeyDown} />
        }
        {type === 'password' && 
        <img
        className='icon password-icon'
        src={showPassword ? closedEyeIcon : openedEyeIcon}
        onClick={() => togglePassword(!showPassword)}
        alt='password-eye-icon' />}
      </div>
    </div>
  )
}